import { useState } from 'react';
import type { Device } from '@glyph/protocol-ts';
import { useCan, useSendDeepgramKey } from '@glyph/core';
import { Button, Card, CardHeader, Checkbox, Note, SegmentedControl, TextInput, useToast } from '@glyph/ui';

/**
 * Push one Deepgram API key from the console to every unit or a chosen set (admin). The unit stores it as its
 * local key and restarts captioning; offline units receive it when they reconnect. Nothing is kept in the cloud.
 */
export function DeepgramKeyCard({ devices, className }: { devices: Device[]; className?: string }) {
  const can = useCan('credentials.deepgram.set');
  const send = useSendDeepgramKey();
  const toast = useToast();
  const [key, setKey] = useState('');
  const [target, setTarget] = useState<'all' | 'selected'>('all');
  const [sel, setSel] = useState<string[]>([]);
  if (!can) return null;
  const ready = key.trim().length >= 20 && (target === 'all' ? devices.length > 0 : sel.length > 0);
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const submit = async () => {
    try {
      const r = await send.send(target === 'all' ? { api_key: key.trim(), all: true } : { api_key: key.trim(), device_ids: sel });
      const sent = r.results.filter((x) => x.status === 'sent').length;
      const queued = r.results.filter((x) => x.status === 'queued').length;
      const failed = r.results.filter((x) => x.status === 'error' || x.status === 'forbidden');
      toast.push(`Deepgram key: ${sent} sent${queued ? `, ${queued} queued until online` : ''}${failed.length ? `, ${failed.length} failed` : ''}`, failed.length ? 'warn' : 'ok');
      setKey('');
    } catch (e) {
      toast.push((e as Error).message || 'Could not send the key', 'bad');
    }
  };
  return (
    <Card className={className}>
      <CardHeader title="Deepgram key" meta={target === 'all' ? `ALL ${devices.length} UNITS` : `${sel.length} SELECTED`} />
      <div className="flex flex-col gap-3 border-t border-line px-4 py-3">
        <TextInput block variant="mono" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Deepgram API key" aria-label="Deepgram API key" />
        <SegmentedControl
          fullWidth
          value={target}
          onChange={setTarget}
          options={[
            { value: 'all', label: 'All units' },
            { value: 'selected', label: 'Selected' },
          ]}
          aria-label="Target"
        />
        {target === 'selected' && (
          <div className="flex flex-col gap-1">
            {devices.map((d) => (
              <div key={d.id} className="flex items-center gap-2 py-1 text-base">
                <Checkbox checked={sel.includes(d.id)} onChange={() => toggle(d.id)} label={`Send to ${d.name}`} />
                <span className="min-w-0 flex-1 truncate">{d.name}</span>
                <span className="font-mono text-xs text-fg4">{d.online ? 'online' : 'queued when online'}</span>
              </div>
            ))}
          </div>
        )}
        <Button variant="primary" size="responsive" disabled={!ready || send.isPending} onClick={() => void submit()}>
          {send.isPending ? 'Sending…' : target === 'all' ? 'Send to all units' : 'Send to selected'}
        </Button>
        <Note>Each unit stores it as its own key and restarts captioning. Units that are off get it the moment they reconnect. The key is not kept in the cloud.</Note>
      </div>
    </Card>
  );
}
